import { useId } from 'react';
import { cn } from '@/lib/utils';

export interface SparklineProps {
  values: readonly number[];
  /** Tints the fill with the direction of travel. */
  trend?: 'up' | 'down' | 'flat';
  className?: string;
}

const STROKE: Record<'up' | 'down' | 'flat', string> = {
  up: 'text-success',
  down: 'text-destructive',
  flat: 'text-muted-foreground',
};

/**
 * Hand-drawn SVG rather than a chart library: at 64x24 with no axes, ticks or
 * tooltip, Recharts would be several kilobytes of machinery doing nothing.
 */
export function Sparkline({ values, trend = 'flat', className }: SparklineProps) {
  const gradientId = useId();
  if (values.length < 2) return null;

  const width = 100;
  const height = 32;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = width / (values.length - 1);

  const points = values.map((value, index) => {
    const x = index * step;
    const y = height - ((value - min) / span) * (height - 4) - 2;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden
      className={cn('h-8 w-full', STROKE[trend], className)}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,${height} ${points.join(' ')} ${width},${height}`}
        fill={`url(#${gradientId})`}
      />
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
